import { AttributeOption } from '@deltager/organizers/admin/modules/events/create-event/model/attribute-option.model';
import { GeneralAttribute } from '@deltager/organizers/admin/modules/events/create-event/model/generalAttribute.model';
import { AttributeRelation } from '@deltager/organizers/admin/modules/events/create-event/model/attribute-relation.model';
import { QuestionType } from './question-type.enum';
import { AttributeType } from './attribute-type.enum';
import { EventAttributeDropdownType } from '../../../../shared/models/attribute-dropdown-type.enum';
import { UploadProperties } from './attribute-upload.model';
import { Event } from './event.model';
import { ExternalLoginSystem } from './external-login-system.model';

export class Attribute {
  id?: number;
  name: string;
  priority?: number;
  type?: QuestionType;
  dropdownType: EventAttributeDropdownType;
  isShowAttrType?: boolean;
  options?: AttributeOption[];
  active: boolean;
  mandatory: boolean;
  showOnReceipt?: boolean;
  askOncePerOrder?: boolean;
  showOnParticipantsList?: boolean;
  showOnInstructorsList?: boolean;
  copyAnswerToAllParticipants?: boolean;
  changeByAdminOnly?: boolean;
  includeInInvitations?: boolean;
  description?: string;
  uiId?: number;
  vat?: number;
  checkboxPrice?: number;
  checkboxNonRefundable: boolean;
  productId?: number;
  dependencies?: AttributeRelation[];
  multiline: boolean;
  onlyNumbers: boolean;
  isDisableForSelect?: boolean;
  uploadProperties?: UploadProperties;
  isLimitNumberOfCharacters: boolean;
  maxNumberOfCharacters: number;
  customValidationId: number;
  addToUserProfile?: boolean;
  isMembershipNumber: boolean;
  maxAmountOfRegistrations: number;
  registered: number;
  isParticipantEditingEnabled: boolean;

  static buildAttribute(uiId: number, name: string, priority: number, questionType: QuestionType, optionName: string) {
    const attribute = new Attribute();
    attribute.id = 0;
    attribute.name = name;
    attribute.priority = priority;
    attribute.type = questionType;
    attribute.active = true;
    attribute.mandatory = false;
    attribute.showOnReceipt = false;
    attribute.askOncePerOrder = false;
    attribute.showOnParticipantsList = true;
    attribute.showOnInstructorsList = false;
    attribute.copyAnswerToAllParticipants = false;
    attribute.changeByAdminOnly = false;
    attribute.includeInInvitations = false;
    attribute.uiId = uiId;
    attribute.description = '';
    attribute.dependencies = [];
    attribute.multiline = false;
    attribute.onlyNumbers = false;
    attribute.maxAmountOfRegistrations = 0;
    attribute.registered = 0;

    switch (questionType) {
      case QuestionType.DROPDOWN: {
        attribute.dropdownType = EventAttributeDropdownType.dropdown;
        attribute.options = [AttributeOption.BuildOption(1, optionName)];
        break;
      }
      case QuestionType.CHECKBOX: {
        attribute.options = [];
        attribute.checkboxPrice = 0;
        break;
      }
      case QuestionType.TEXT:
      case QuestionType.DATE_PICKER:
      case QuestionType.CONTENT: {
        attribute.options = [];
        break;
      }
    }

    return attribute;
  }

  static transformToAttribute(generalAttr: GeneralAttribute, checkRemovedOptions = false) {
    const transformedAttribute: Attribute = {
      id: generalAttr.id,
      name: generalAttr.name,
      active: generalAttr.active,
      priority: generalAttr.priority,
      type: generalAttr.type,
      options: generalAttr.options.map(o => (checkRemovedOptions && !o ? undefined : new AttributeOption(o))),
      mandatory: generalAttr.mandatory,
      showOnReceipt: generalAttr.showOnReceipt,
      askOncePerOrder: generalAttr.askOncePerOrder,
      showOnParticipantsList: generalAttr.showOnParticipantsList,
      showOnInstructorsList: generalAttr.showOnInstructorsList,
      copyAnswerToAllParticipants: generalAttr.copyAnswerToAllParticipants,
      changeByAdminOnly: generalAttr.changeByAdminOnly,
      includeInInvitations: generalAttr.includeInInvitations,
      uiId: generalAttr.uiId,
      dependencies: generalAttr.dependencies,
      description: generalAttr.description,
      vat: generalAttr.vat === null ? 0 : generalAttr.vat,
      multiline: generalAttr.multiline,
      onlyNumbers: generalAttr.onlyNumbers,
      checkboxPrice: generalAttr.checkboxPrice === null ? 0 : generalAttr.checkboxPrice,
      checkboxNonRefundable: generalAttr.checkboxNonRefundable,
      productId: generalAttr.productId,
      dropdownType: generalAttr.dropdownType,
      uploadProperties: generalAttr.uploadProperties,
      isLimitNumberOfCharacters: generalAttr.isLimitNumberOfCharacters,
      maxNumberOfCharacters: generalAttr.maxNumberOfCharacters,
      customValidationId: generalAttr.customValidationId,
      addToUserProfile: generalAttr.addToUserProfile,
      isMembershipNumber: generalAttr.isMembershipNumber,
      maxAmountOfRegistrations: generalAttr.maxAmountOfRegistrations,
      registered: generalAttr.registered,
      isParticipantEditingEnabled: generalAttr.isParticipantEditingEnabled,
    };
    return transformedAttribute;
  }

  static transformToGeneralAttribute(attribute: Attribute, attributes: Attribute[], event: Event): GeneralAttribute {
    let generalAttribute = new GeneralAttribute();
    generalAttribute.id = attribute.id;
    generalAttribute.name = attribute.name;
    generalAttribute.active = attribute.active;
    generalAttribute.type = attribute.type;
    generalAttribute.priority = attribute.priority;
    generalAttribute.mandatory = attribute.mandatory;
    generalAttribute.showOnReceipt = attribute.showOnReceipt;
    generalAttribute.options = attribute.options
      ? attribute.options.map(opt => {
          if (!opt) return undefined;

          const hasAttributeDependencies = attributes.some(
            v =>
              v !== undefined &&
              v.dependencies !== undefined &&
              v.dependencies.some(d => d !== undefined && d.dependentAttributeDetailsId === opt.id),
          );

          return AttributeOption.transformToAttributeOption(opt, hasAttributeDependencies);
        })
      : undefined;
    generalAttribute.typeOfAttr = AttributeType.customAttribute;
    generalAttribute.askOncePerOrder = attribute.askOncePerOrder;
    generalAttribute.showOnParticipantsList = attribute.showOnParticipantsList;
    generalAttribute.showOnInstructorsList = attribute.showOnInstructorsList;
    generalAttribute.copyAnswerToAllParticipants = attribute.copyAnswerToAllParticipants;
    generalAttribute.changeByAdminOnly = attribute.changeByAdminOnly;
    generalAttribute.includeInInvitations = attribute.includeInInvitations;
    generalAttribute.uiId = attribute.uiId;
    generalAttribute.isBuiltinAttr = false;
    generalAttribute.dependencies = attribute.dependencies;
    generalAttribute.description = attribute.description;
    generalAttribute.vat = attribute.vat;
    generalAttribute.multiline = attribute.multiline;
    generalAttribute.onlyNumbers = attribute.onlyNumbers;
    generalAttribute.isShowAttrType = attribute.isShowAttrType;
    generalAttribute.checkboxPrice = attribute.checkboxPrice;
    generalAttribute.checkboxNonRefundable = attribute.checkboxNonRefundable;
    generalAttribute.productId = attribute.productId;

    generalAttribute.hasAttributeDependencies = this.hasAttributeDependencies(attribute, attributes);

    generalAttribute.dropdownType = attribute.dropdownType;
    generalAttribute.uploadProperties = attribute.uploadProperties;
    generalAttribute.isLimitNumberOfCharacters = attribute.isLimitNumberOfCharacters;
    generalAttribute.maxNumberOfCharacters = attribute.maxNumberOfCharacters;
    generalAttribute.customValidationId = attribute.customValidationId;
    generalAttribute.addToUserProfile = attribute.addToUserProfile;
    generalAttribute.isMembershipNumber = attribute.isMembershipNumber;
    generalAttribute.maxAmountOfRegistrations = attribute.maxAmountOfRegistrations;
    generalAttribute.registered = attribute.registered;

    generalAttribute.locked =
      attribute.isMembershipNumber && event.useExternalLogin && event.externalLoginSystem === ExternalLoginSystem.DNT;
    generalAttribute.isParticipantEditingEnabled = attribute.isParticipantEditingEnabled;

    return generalAttribute;
  }

  private static hasAttributeDependencies(attribute: Attribute, attributes: Attribute[]): boolean {
    const hasDependencies = attributes.some(
      attr =>
        attr !== undefined &&
        attr.dependencies !== undefined &&
        attr.dependencies.some(d => d !== undefined && d.dependentAttributeId === attribute.id),
    );

    const existsRegisteredOptions = attribute.options && attribute.options.some(op => op && op.registered > 0);

    return hasDependencies || existsRegisteredOptions;
  }
}
